import { fitText, textWidth } from '../ComboChart/labels';
import { valueScale } from '../ComboChart/scale';

export interface RadarSeries {
  key: string;
  color: string;
  /** One per spoke; undefined (no value) draws at the center. */
  values: (number | undefined)[];
  /** The data label of each value, when data labels are on. */
  labels?: (string | undefined)[];
}

export interface RadarLayoutInput {
  width: number;
  height: number;
  spokes: string[];
  series: RadarSeries[];
  /** Each series against its own maximum, not one shared scale. */
  perSeries: boolean;
  fontSize: number;
  /** A shared scale reaches at least this (a score's 100). */
  scaleFloor?: number;
}

export interface RadarLayout {
  cx: number;
  cy: number;
  radius: number;
  /** Each spoke's angle, clockwise from straight up, in radians. */
  angles: number[];
  /** The spoke names as drawn (cut to fit). */
  names: string[];
  /** The shared scale's ring values, outermost last; empty per series. */
  rings: number[];
  /** The value the outer ring stands for on a shared scale. */
  sharedMax: number | null;
  /** The value the outer ring stands for, per series. */
  maxOf: number[];
}

/** Rings of the web, the outer one included. */
export const kRadarRings = 4;

/** A point `r` from the center along `angle`. */
export function radarPoint(
  layout: Pick<RadarLayout, 'cx' | 'cy'>,
  angle: number,
  r: number,
): { x: number; y: number } {
  return {
    x: layout.cx + r * Math.sin(angle),
    y: layout.cy - r * Math.cos(angle),
  };
}

function finite(values: (number | undefined)[]): number[] {
  return values.filter(
    (value): value is number => value !== undefined && Number.isFinite(value),
  );
}

/**
 * Where the web, its spoke names and the scale go. A shared scale rounds
 * its outer ring up to a tick; per series, each series' largest value is
 * the outer ring. Negative values draw at the center.
 */
export function radarLayout(input: RadarLayoutInput): RadarLayout {
  const { width, height, spokes, series, fontSize: f } = input;
  const count = Math.max(spokes.length, 1);
  const angles = spokes.map((_, i) => (2 * Math.PI * i) / count);
  const names = spokes.map(name => fitText(name, width * 0.24, f));
  // Names left and right of the web need their width; above and below
  // their line height.
  const side = Math.max(
    0,
    ...names.map((name, i) =>
      Math.abs(Math.sin(angles[i])) > 0.3 ? textWidth(name, f) : 0,
    ),
  );
  const radius = Math.max(
    Math.min(width / 2 - side - 8, height / 2 - f * 1.6),
    16,
  );
  const shared = input.perSeries
    ? null
    : valueScale(
        series
          .flatMap(entry => finite(entry.values))
          .map(v => Math.max(v, 0))
          .concat(input.scaleFloor ?? []),
        radius,
        { min: 0 },
        kRadarRings,
      );
  return {
    cx: width / 2,
    cy: height / 2,
    radius,
    angles,
    names,
    rings: shared ? shared.ticks.filter(tick => tick > 0) : [],
    sharedMax: shared ? shared.max : null,
    maxOf: series.map(entry =>
      shared ? shared.max : Math.max(0, ...finite(entry.values)) || 1,
    ),
  };
}
